import { useCallback, useEffect, useState } from 'react';

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import type { JwtPayload } from 'jwt-decode';
import { jwtDecode } from 'jwt-decode';

import api from '@/lib/api';
import type { User } from '@/types';

import { AuthContext, type AuthProviderProps } from './auth-context';

export const AuthProvider = ({ children }: AuthProviderProps) => {
  const queryClient = useQueryClient();
  const [token, setToken] = useState<string | null>(localStorage.getItem('token'));

  // Query for current user profile
  const {
    data: profileData,
    isLoading: profileLoading,
    error: profileError,
  } = useQuery({
    queryKey: ['auth', 'me'],
    queryFn: async () => {
      const { data } = await api.get('/auth/me');
      return data;
    },
    enabled: !!token,
    retry: false,
  });

  // Logout function
  const logout = useCallback(() => {
    localStorage.removeItem('token');
    setToken(null);
    queryClient.setQueryData(['auth', 'me'], null);
    queryClient.invalidateQueries({ queryKey: ['auth'] });
  }, [queryClient]);

  // Handle token expiration and profile errors
  useEffect(() => {
    if (token) {
      try {
        const decoded = jwtDecode<JwtPayload>(token);
        if (decoded.exp && decoded.exp * 1000 < Date.now()) {
          logout();
        }
      } catch (_) {
        logout();
      }
    }
  }, [token, logout]);

  useEffect(() => {
    if (profileError) {
      logout();
    }
  }, [profileError, logout]);

  // Mutations for auth actions
  const loginMutation = useMutation({
    mutationFn: async ({ email, password }: { email: string; password: string }) => {
      const { data } = await api.post('/auth/login', { email, password });
      return data;
    },
    onSuccess: (data) => {
      const { access_token, user } = data;
      localStorage.setItem('token', access_token);
      setToken(access_token);
      queryClient.setQueryData(['auth', 'me'], user);
    },
  });

  const registerMutation = useMutation({
    mutationFn: async (registerData: { email: string; name: string; password: string; role: string }) => {
      const { data } = await api.post('/auth/register', registerData);
      return data;
    },
    onSuccess: (data) => {
      const { access_token, user } = data;
      localStorage.setItem('token', access_token);
      setToken(access_token);
      queryClient.setQueryData(['auth', 'me'], user);
    },
  });

  const googleLoginMutation = useMutation({
    mutationFn: async (credential: string) => {
      const { data } = await api.post('/auth/google', { credential });
      return data;
    },
    onSuccess: (data) => {
      const { access_token, user } = data;
      localStorage.setItem('token', access_token);
      setToken(access_token);
      queryClient.setQueryData(['auth', 'me'], user);
    },
  });

  const login = async (email: string, password: string) => {
    try {
      await loginMutation.mutateAsync({ email, password });
      return { success: true };
    } catch (error) {
      const axiosError = error as AxiosError<{ detail: string }>;
      return { success: false, error: axiosError.response?.data?.detail || 'Sign-in failed.' };
    }
  };

  const register = async (email: string, password: string, name: string, role: string) => {
    try {
      await registerMutation.mutateAsync({ email, password, name, role });
      return { success: true };
    } catch (error) {
      const axiosError = error as AxiosError<{ detail: string }>;
      return { success: false, error: axiosError.response?.data?.detail || 'Registration failed.' };
    }
  };

  const googleLogin = async (credential: string) => {
    try {
      await googleLoginMutation.mutateAsync(credential);
      return { success: true };
    } catch (error) {
      const axiosError = error as AxiosError<{ detail: string }>;
      return { success: false, error: axiosError.response?.data?.detail || 'Google sign-in failed.' };
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user: profileData as User,
        token,
        loading: profileLoading && !!token,
        login,
        register,
        googleLogin,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
